import { NavLink } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { Theme } from '@lineageatlas/design-system';

type NavIconName =
  | 'grid'
  | 'registry'
  | 'lot'
  | 'intel'
  | 'study'
  | 'physician'
  | 'provider'
  | 'alert'
  | 'shield'
  | 'history'
  | 'compare'
  | 'copilot'
  | 'settings'
  | 'moon'
  | 'sun';

interface NavItem {
  path: string;
  label: string;
  prototypeLabel?: string;
  icon: NavIconName;
  badge?: string;
  exact?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    label: 'Platform',
    items: [
      { path: '/console', label: 'Command Center', icon: 'grid', exact: true },
      {
        path: '/console/outcomes-registry',
        label: 'Outcomes Registry',
        icon: 'registry',
        badge: '1,284',
      },
      {
        path: '/console/lots',
        label: 'Lots',
        prototypeLabel: 'Lot Lineage',
        icon: 'lot',
        badge: '24',
      },
      {
        path: '/console/intelligence',
        label: 'Intelligence',
        prototypeLabel: 'Signals to investigate',
        icon: 'intel',
        badge: '3',
      },
      { path: '/console/compare', label: 'Compare', icon: 'compare' },
      {
        path: '/console/ai-copilot',
        label: 'AI Co-pilot',
        prototypeLabel: 'AI Co-pilot',
        icon: 'copilot',
      },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        path: '/console/studies',
        label: 'Studies',
        prototypeLabel: 'Study Management',
        icon: 'study',
      },
      {
        path: '/console/investigators',
        label: 'Investigators',
        prototypeLabel: 'Physicians',
        icon: 'physician',
      },
      {
        path: '/console/providers',
        label: 'Providers',
        prototypeLabel: 'Provider Network',
        icon: 'provider',
      },
      {
        path: '/console/adverse-events',
        label: 'Adverse Events',
        icon: 'alert',
        badge: '1',
      },
      { path: '/console/consent-access', label: 'Consent & Access', icon: 'shield' },
      { path: '/console/audit', label: 'Audit', prototypeLabel: 'Audit Log', icon: 'history' },
    ],
  },
];

function Icon({ name }: { name: NavIconName }) {
  const paths: Record<NavIconName, ReactNode> = {
    grid: (
      <>
        <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z" />
      </>
    ),
    registry: (
      <>
        <path d="M5 4h14v16H5z" />
        <path d="M8 8h8M8 12h8M8 16h5" />
      </>
    ),
    lot: (
      <>
        <path d="M7 3h10M9 3v6l-4.5 8A3 3 0 0 0 7.1 21h9.8a3 3 0 0 0 2.6-4L15 9V3" />
        <path d="M8 14h8" />
      </>
    ),
    intel: (
      <>
        <path d="M12 3 14 8l5 2-5 2-2 5-2-5-5-2 5-2 2-5Z" />
        <path d="M19 15v4M17 17h4" />
      </>
    ),
    study: (
      <>
        <path d="M6 4h9l3 3v13H6z" />
        <path d="M15 4v4h4M9 12h6M9 16h6" />
      </>
    ),
    physician: (
      <>
        <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" />
        <path d="M5 21a7 7 0 0 1 14 0M12 15v5M9.5 17.5h5" />
      </>
    ),
    provider: (
      <>
        <path d="M4 20V8l8-4 8 4v12" />
        <path d="M9 20v-6h6v6M8 10h.01M12 10h.01M16 10h.01" />
      </>
    ),
    alert: (
      <>
        <path d="M12 3 22 20H2L12 3Z" />
        <path d="M12 9v5M12 17h.01" />
      </>
    ),
    shield: (
      <>
        <path d="M12 3 20 6v6c0 5-3.4 8-8 9-4.6-1-8-4-8-9V6l8-3Z" />
        <path d="m9 12 2 2 4-5" />
      </>
    ),
    history: (
      <>
        <path d="M4 12a8 8 0 1 0 2.3-5.7L4 8.5" />
        <path d="M4 4v4.5h4.5M12 7v5l3 2" />
      </>
    ),
    compare: (
      <>
        <path d="M4 7h10M4 17h16" />
        <path d="M14 4l3 3-3 3M10 14l-3 3 3 3" />
      </>
    ),
    copilot: (
      <>
        <path d="M12 3 14 8l5 2-5 2-2 5-2-5-5-2 5-2 2-5Z" />
        <path d="M18 16.5 19 19l2.5 1L19 21l-1 2.5-1-2.5-2.5-1 2.5-1 1-2.5Z" />
      </>
    ),
    settings: (
      <>
        <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" />
        <path d="M19.4 15a8 8 0 0 0 .1-1l2-1.5-2-3.5-2.4 1a8 8 0 0 0-1.7-1L15 6.5h-4l-.4 2.5a8 8 0 0 0-1.7 1L6.5 9l-2 3.5 2 1.5a8 8 0 0 0 .1 1l-2.1 1.5 2 3.5 2.4-1a8 8 0 0 0 1.7 1l.4 2.5h4l.4-2.5a8 8 0 0 0 1.7-1l2.4 1 2-3.5L19.4 15Z" />
      </>
    ),
    moon: <path d="M20.4 14.4A7 7 0 0 1 9.6 3.6a8 8 0 1 0 10.8 10.8Z" />,
    sun: (
      <>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
      </>
    ),
  };

  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

interface SidebarProps {
  theme: Theme;
  onThemeToggle: () => void;
  onMobileClose?: () => void;
}

export function Sidebar({ theme, onThemeToggle, onMobileClose }: SidebarProps) {
  return (
    <aside className="rl-sidebar" aria-label="Lab Console navigation">
      <NavLink
        className="rl-brand"
        to="/console"
        onClick={onMobileClose}
        aria-label="Command Center"
        style={{ color: 'inherit', textDecoration: 'none' }}
      >
        <svg className="mk" viewBox="0 0 32 32" fill="none" aria-hidden="true">
          <circle
            cx="16"
            cy="16"
            r="11"
            stroke="currentColor"
            strokeOpacity="0.35"
            strokeWidth="1.5"
          />
          <path
            d="M16 5a11 11 0 0 1 9.5 5.5"
            stroke="var(--accent)"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="16" cy="16" r="3.4" fill="var(--accent)" />
          <circle cx="25.5" cy="10.5" r="2" fill="var(--accent)" />
        </svg>
        <span className="nm">
          RegenLoop<span className="ai">.ai</span>
        </span>
      </NavLink>

      {navGroups.map((group) => (
        <div key={group.label}>
          <div className="rl-navlabel">{group.label}</div>
          <nav className="rl-nav" aria-label={`${group.label} navigation`}>
            {group.items.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.exact}
                onClick={onMobileClose}
                aria-label={item.label}
                className={({ isActive }) => (isActive ? 'on' : undefined)}
                style={{ textDecoration: 'none' }}
              >
                <Icon name={item.icon} />
                <span className="lb">{item.prototypeLabel ?? item.label}</span>
                {item.badge ? <span className="cnt num">{item.badge}</span> : null}
              </NavLink>
            ))}
          </nav>
        </div>
      ))}

      <div className="rl-side-foot">
        <nav className="rl-nav" aria-label="Workspace navigation">
          <NavLink
            to="/console/settings"
            onClick={onMobileClose}
            aria-label="Settings"
            className={({ isActive }) => (isActive ? 'on' : undefined)}
            style={{ textDecoration: 'none' }}
          >
            <Icon name="settings" />
            <span className="lb">Settings</span>
          </NavLink>
          <button className="rl-navbtn" type="button" onClick={onThemeToggle}>
            <Icon name={theme === 'light' ? 'moon' : 'sun'} />
            <span className="lb">{theme === 'light' ? 'Dark mode' : 'Light mode'}</span>
          </button>
        </nav>

        <div className="rl-userpill">
          <span className="rl-avatar">DM</span>
          <div>
            <div className="u-nm">Dr. Mara Okonkwo</div>
            <div className="u-rl">Principal Investigator</div>
          </div>
        </div>

        <div
          style={{
            color: 'var(--faint)',
            fontFamily: 'var(--mono)',
            fontSize: 9,
            letterSpacing: '0.01em',
            lineHeight: 1.55,
            padding: '11px 8px 0',
          }}
        >
          Research &amp; quality-support intelligence. Not medical advice or a diagnostic device.
        </div>
      </div>
    </aside>
  );
}
